import { api } from ".";
import {
  BaseRateOverrideRuleType,
  FeeScheduleType,
  IASCWageIndexEntry,
  IBaseRateFile,
  IBaseRateOverrideItem,
  ICmsPreviewResponse,
  ICmsPsfOverrides,
  ICmsToggleConfig,
  IGpciLocality,
  IIppsImpactHospital,
  IIPPSStandardizedAmounts,
  IPsfInpatientHospital,
  ISourceDataStatus,
} from "../../types/baseRateFile";

export interface ICreateOverrideItemInput {
  name: string;
  description?: string;
  ruleType: BaseRateOverrideRuleType;
  feeScheduleType: FeeScheduleType;
  isEnabled?: boolean;
  priority?: number;
  hcpcs: string;
  mod?: string;
  overrideRate?: number;
  sourceOverride?: string;
  swapWithHcpcs?: string;
  dmeposPriceColumn?: string;
  notes?: string;
}

export interface IUpdateOverrideItemInput {
  name?: string;
  description?: string;
  ruleType?: BaseRateOverrideRuleType;
  feeScheduleType?: FeeScheduleType;
  isEnabled?: boolean;
  priority?: number;
  hcpcs?: string;
  mod?: string;
  overrideRate?: number;
  sourceOverride?: string;
  swapWithHcpcs?: string;
  dmeposPriceColumn?: string;
  notes?: string;
}

const baseRateFilesQueries = api.injectEndpoints({
  endpoints: (builder) => ({
    getAllBaseRateFiles: builder.query<
      { data: IBaseRateFile[]; message: string },
      { search?: string; accountId?: string }
    >({
      query: (params) => ({
        url: "/base-rate-files",
        params,
      }),
      providesTags: ["BaseRateFiles"],
    }),
    getBaseRateFileById: builder.query<
      { data: IBaseRateFile; message: string },
      string
    >({
      query: (id) => `/base-rate-files/${id}`,
    }),
    getBaseRateFileTerms: builder.query<
      IBaseRateFile[],
      { fileName: string; accountId?: string }
    >({
      query: ({ fileName, accountId }) => ({
        url: "/base-rate-files/terms",
        params: { fileName, ...(accountId ? { accountId } : {}) },
      }),
      transformResponse: (
        response: { data: IBaseRateFile[]; message?: string } | IBaseRateFile[]
      ) => {
        const raw = Array.isArray(response) ? response : response?.data ?? [];
        return raw.map((row) => {
          const r = row as IBaseRateFile & { effective_date?: string };
          const fromSnake = r.effective_date;
          if (
            r.effectiveDate == null &&
            fromSnake != null &&
            fromSnake !== ""
          ) {
            return { ...r, effectiveDate: String(fromSnake) };
          }
          return row;
        });
      },
    }),
    uploadBaseRateFile: builder.mutation<
      { data: IBaseRateFile; message: string },
      {
        file: File;
        displayName: string;
        description?: string;
        accountId?: string;
        /** ISO date (yyyy-mm-dd) for this base rate term. */
        effectiveDate?: string;
        isNewTerm?: boolean;
        activateContracts?: boolean;
        contractEffectiveDate?: string;
        isCarveout?: boolean;
      }
    >({
      query: ({
        file,
        displayName,
        description,
        accountId,
        effectiveDate,
        isNewTerm,
        activateContracts,
        contractEffectiveDate,
        isCarveout,
      }) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("displayName", displayName);
        if (description) {
          formData.append("description", description);
        }
        if (accountId) {
          formData.append("accountId", accountId);
        }
        if (effectiveDate) {
          formData.append("effectiveDate", effectiveDate);
        }
        if (isCarveout === true) {
          formData.append("isCarveout", "true");
        }
        if (isNewTerm === true) {
          formData.append("isNewTerm", "true");
        }
        if (activateContracts === true) {
          formData.append("activateContracts", "true");
        }
        if (contractEffectiveDate) {
          formData.append("contractEffectiveDate", contractEffectiveDate);
        }

        return {
          url: "/base-rate-files/upload",
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["BaseRateFiles"],
    }),
    updateBaseRateFile: builder.mutation<
      { data: IBaseRateFile; message: string },
      {
        id: string;
        file: File;
        displayName: string;
        description?: string;
        accountId?: string;
        isNewTerm?: boolean;
        effectiveDate?: string;
        activateContracts?: boolean;
        contractEffectiveDate?: string;
        /** When true (with activateContracts false), API should create Draft term(s) instead of only flagging outdated. */
        createDraftContracts?: boolean;
        /** Same-term version updates only: kick off background regeneration of attached active contracts. */
        regenerateActiveContracts?: boolean;
      }
    >({
      query: ({
        id,
        file,
        displayName,
        description,
        accountId,
        isNewTerm,
        effectiveDate,
        activateContracts,
        contractEffectiveDate,
        createDraftContracts,
        regenerateActiveContracts,
      }) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("displayName", displayName);
        if (description) {
          formData.append("description", description);
        }
        if (accountId) {
          formData.append("accountId", accountId);
        }
        if (isNewTerm === true) {
          formData.append("isNewTerm", "true");
        }
        if (effectiveDate) {
          formData.append("effectiveDate", effectiveDate);
        }
        if (activateContracts === true) {
          formData.append("activateContracts", "true");
        }
        if (contractEffectiveDate) {
          formData.append("contractEffectiveDate", contractEffectiveDate);
        }
        if (createDraftContracts === true) {
          formData.append("createDraftContracts", "true");
        }
        if (regenerateActiveContracts === true) {
          formData.append("regenerateActiveContracts", "true");
        }

        return {
          url: `/base-rate-files/${id}/update`,
          method: "PUT",
          body: formData,
        };
      },
      invalidatesTags: ["BaseRateFiles", "ContractFeeSchedules"],
    }),
    getBaseRateFileRegenerationStatus: builder.query<
      {
        data: Record<
          string,
          {
            inProgress: boolean;
            total: number;
            completed: number;
            failedContractIds: string[];
          }
        >;
        message: string;
      },
      { ids: string[] }
    >({
      query: ({ ids }) => ({
        url: "/base-rate-files/regeneration-status",
        params: { ids: ids.join(",") },
      }),
    }),
    deleteBaseRateFile: builder.mutation<{ message: string }, string>({
      query: (id) => ({
        url: `/base-rate-files/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["BaseRateFiles"],
    }),
    getBaseRateFileDownloadUrl: builder.query<
      { url: string; message: string },
      string
    >({
      query: (id) => `/base-rate-files/${id}/download-url`,
    }),
    getSourceStatus: builder.query<
      { data: ISourceDataStatus; message: string },
      void
    >({
      query: () => "/base-rate-files/source/status",
    }),
    uploadAddendumBSource: builder.mutation<
      { data: unknown; message: string },
      { file: File; year: number }
    >({
      query: ({ file, year }) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("year", String(year));

        return {
          url: "/base-rate-files/source/addendum-b",
          method: "POST",
          body: formData,
        };
      },
    }),
    uploadAddendumAASource: builder.mutation<
      { data: unknown; message: string },
      { file: File; year: number }
    >({
      query: ({ file, year }) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("year", String(year));

        return {
          url: "/base-rate-files/source/addendum-aa",
          method: "POST",
          body: formData,
        };
      },
    }),
    uploadClfsSource: builder.mutation<
      { data: unknown; message: string },
      { file: File; year: number }
    >({
      query: ({ file, year }) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("year", String(year));

        return {
          url: "/base-rate-files/source/clfs",
          method: "POST",
          body: formData,
        };
      },
    }),
    uploadAspSource: builder.mutation<
      { data: unknown; message: string },
      { file: File; year: number }
    >({
      query: ({ file, year }) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("year", String(year));

        return {
          url: "/base-rate-files/source/asp",
          method: "POST",
          body: formData,
        };
      },
    }),
    uploadDmeposSource: builder.mutation<
      { data: unknown; message: string },
      { file: File; year: number }
    >({
      query: ({ file, year }) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("year", String(year));

        return {
          url: "/base-rate-files/source/dmepos",
          method: "POST",
          body: formData,
        };
      },
    }),
    uploadPprvuSource: builder.mutation<
      { data: unknown; message: string },
      { file: File; year: number }
    >({
      query: ({ file, year }) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("year", String(year));

        return {
          url: "/base-rate-files/source/pprvu",
          method: "POST",
          body: formData,
        };
      },
    }),
    uploadIppsSource: builder.mutation<
      { data: unknown; message: string },
      { file: File; year: number }
    >({
      query: ({ file, year }) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("year", String(year));

        return {
          url: "/base-rate-files/source/ipps",
          method: "POST",
          body: formData,
        };
      },
    }),
    uploadIppsStdAmountsSource: builder.mutation<
      { data: unknown; message: string },
      { file: File; year: number }
    >({
      query: ({ file, year }) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("year", String(year));

        return {
          url: "/base-rate-files/source/ipps-std-amounts",
          method: "POST",
          body: formData,
        };
      },
    }),
    uploadIppsImpactSource: builder.mutation<
      { data: { year: number; rowsProcessed: number }; message: string },
      { file: File; year: number }
    >({
      query: ({ file, year }) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("year", String(year));

        return {
          url: "/base-rate-files/source/ipps-impact",
          method: "POST",
          body: formData,
        };
      },
    }),
    searchIppsImpactHospitals: builder.query<
      { data: IIppsImpactHospital[]; message: string },
      { year: number; q: string }
    >({
      query: ({ year, q }) => ({
        url: `/base-rate-files/ipps-impact/search?year=${year}&q=${encodeURIComponent(
          q
        )}`,
      }),
    }),
    /**
     * Fetch IPPS PSF (Provider Specific File) snapshot from CMS Provider Data
     * Service API, filtered to the selected calendar quarter (1-4). No file
     * upload — server streams ~277 MB CSV from https://pds.mps.cms.gov and
     * persists ~16k provider rows. Takes 1–5 minutes; the call resolves only
     * after the full ingest completes.
     */
    fetchPsfInpatientSource: builder.mutation<
      {
        data: {
          year: number;
          quarter: number | null;
          rowsProcessed: number;
          fetchedAt: string;
        };
        message: string;
      },
      { year: number; quarter?: number }
    >({
      query: ({ year, quarter }) => {
        const qs =
          quarter !== undefined && quarter !== null
            ? `&quarter=${quarter}`
            : "";
        return {
          url: `/base-rate-files/source/psf-inpatient/fetch?year=${year}${qs}`,
          method: "POST",
        };
      },
    }),
    /** Hospital picker for the Hospital Inpatient CMS modal. */
    searchPsfInpatientHospitals: builder.query<
      { data: IPsfInpatientHospital[]; message: string },
      { year: number; q: string }
    >({
      query: ({ year, q }) => ({
        url: `/base-rate-files/psf-inpatient/search?year=${year}&q=${encodeURIComponent(
          q
        )}`,
      }),
    }),
    /**
     * Preview a CMS pricer run for a single hospital + year. Returns:
     *   - raw PSF row (for editable input fields)
     *   - derived factors (read-only summary block)
     *   - sample priced DRGs (for sanity-check)
     */
    previewCmsInpatient: builder.mutation<
      { data: ICmsPreviewResponse; message: string },
      {
        year: number;
        providerCcn: string;
        overrides?: ICmsPsfOverrides;
        toggles?: ICmsToggleConfig;
        drgSampleSize?: number;
      }
    >({
      query: (body) => ({
        url: "/base-rate-files/cms/preview",
        method: "POST",
        body,
      }),
    }),
    uploadGpciSource: builder.mutation<
      { data: { year: number; rowsProcessed: number }; message: string },
      { file: File; year: number }
    >({
      query: ({ file, year }) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("year", String(year));

        return {
          url: "/base-rate-files/source/gpci",
          method: "POST",
          body: formData,
        };
      },
    }),
    searchGpciLocalities: builder.query<
      { data: IGpciLocality[]; message: string },
      { year: number; q: string }
    >({
      query: ({ year, q }) => ({
        url: `/base-rate-files/gpci/search?year=${year}&q=${encodeURIComponent(
          q
        )}`,
      }),
    }),
    uploadAscWageIndexSource: builder.mutation<
      { data: { year: number; rowsProcessed: number }; message: string },
      { file: File; year: number }
    >({
      query: ({ file, year }) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("year", String(year));

        return {
          url: "/base-rate-files/source/asc-wage-index",
          method: "POST",
          body: formData,
        };
      },
    }),
    searchAscWageIndex: builder.query<
      { data: IASCWageIndexEntry[]; message: string },
      { year: number; state: string; query: string }
    >({
      query: ({ year, state, query }) => ({
        url: `/base-rate-files/asc-wage-index/search?year=${year}&state=${state}&query=${encodeURIComponent(
          query
        )}`,
      }),
    }),
    getIppsStandardizedAmounts: builder.query<
      { data: IIPPSStandardizedAmounts; message: string },
      {
        year: number;
        tableType: string;
        scenario: string;
        providerNumber?: string;
      }
    >({
      query: ({ year, tableType, scenario, providerNumber }) => {
        let url = `/base-rate-files/ipps-standardized-amounts?year=${year}&tableType=${encodeURIComponent(
          tableType
        )}&scenario=${encodeURIComponent(scenario)}`;
        if (providerNumber) {
          url += `&providerNumber=${encodeURIComponent(providerNumber)}`;
        }
        return { url };
      },
    }),
    generateBaseRateFile: builder.mutation<
      { data: IBaseRateFile; message: string },
      {
        // Required fields
        year: number;
        displayName: string;
        // Optional common fields
        type?: string;
        description?: string;
        accountId?: string;
        // Common adjustments
        applySequester?: boolean;
        // Addendum B (OPPS) adjustments
        wageIndex?: number;
        // DMEPOS adjustments
        dmeposState?: string;
        dmeposPercent?: number;
        // ASP adjustments
        aspPercent?: number;
        // CLFS adjustments
        clfsPercent?: number;
        // PPRVU adjustments
        pprvuPercent?: number;
        // Source selection for HOSPITAL_OPPS_NATIONAL
        includedSources?: string[];
        // Override rules selection (per-generation)
        selectedRuleIds?: string[];
        // ASC parameters (future)
        localWageIndex?: number;
        // Physician parameters
        gpciPw?: number;
        gpciPe?: number;
        gpciMp?: number;
        // Hospital Inpatient parameters
        gaf?: number;
        ippsScenario?: string;
        ippsTchop?: number;
        ippsTchcp?: number;
        ippsDshopp?: number;
        ippsDshcpp?: number;
        ippsUcpPerClaim?: number;
        /** CCN from IPPS Impact hospital pick (same as standardized-amounts providerNumber) */
        ippsImpactProviderNumber?: string;
        // Hospital Inpatient CMS (PSF-driven) parameters
        cmsProviderCcn?: string;
        cmsToggles?: ICmsToggleConfig;
        cmsPsfOverrides?: ICmsPsfOverrides;
      }
    >({
      query: (body) => ({
        url: "/base-rate-files/generate",
        method: "POST",
        body,
      }),
      invalidatesTags: ["BaseRateFiles"],
    }),
    getOverrideItems: builder.query<
      { data: IBaseRateOverrideItem[]; message: string },
      void
    >({
      query: () => "/base-rate-files/override-items",
      providesTags: ["BaseRateOverrideItems"],
    }),
    getOverrideItemById: builder.query<
      { data: IBaseRateOverrideItem; message: string },
      string
    >({
      query: (id) => `/base-rate-files/override-items/${id}`,
      providesTags: ["BaseRateOverrideItems"],
    }),
    createOverrideItem: builder.mutation<
      { data: IBaseRateOverrideItem; message: string },
      ICreateOverrideItemInput
    >({
      query: (body) => ({
        url: "/base-rate-files/override-items",
        method: "POST",
        body,
      }),
      invalidatesTags: ["BaseRateOverrideItems"],
    }),
    updateOverrideItem: builder.mutation<
      { data: IBaseRateOverrideItem; message: string },
      { id: string; data: IUpdateOverrideItemInput }
    >({
      query: ({ id, data }) => ({
        url: `/base-rate-files/override-items/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["BaseRateOverrideItems"],
    }),
    deleteOverrideItem: builder.mutation<{ message: string }, string>({
      query: (id) => ({
        url: `/base-rate-files/override-items/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["BaseRateOverrideItems"],
    }),
  }),
});

export const {
  useGetAllBaseRateFilesQuery,
  useGetBaseRateFileByIdQuery,
  useGetBaseRateFileTermsQuery,
  useUploadBaseRateFileMutation,
  useUpdateBaseRateFileMutation,
  useGetBaseRateFileRegenerationStatusQuery,
  useDeleteBaseRateFileMutation,
  useGetBaseRateFileDownloadUrlQuery,
  useGetSourceStatusQuery,
  useUploadAddendumBSourceMutation,
  useUploadAddendumAASourceMutation,
  useUploadClfsSourceMutation,
  useUploadAspSourceMutation,
  useUploadDmeposSourceMutation,
  useUploadPprvuSourceMutation,
  useUploadIppsSourceMutation,
  useUploadIppsStdAmountsSourceMutation,
  useUploadIppsImpactSourceMutation,
  useSearchIppsImpactHospitalsQuery,
  useFetchPsfInpatientSourceMutation,
  useSearchPsfInpatientHospitalsQuery,
  useLazySearchPsfInpatientHospitalsQuery,
  usePreviewCmsInpatientMutation,
  useUploadGpciSourceMutation,
  useSearchGpciLocalitiesQuery,
  useUploadAscWageIndexSourceMutation,
  useSearchAscWageIndexQuery,
  useGetIppsStandardizedAmountsQuery,
  useGenerateBaseRateFileMutation,
  useGetOverrideItemsQuery,
  useGetOverrideItemByIdQuery,
  useCreateOverrideItemMutation,
  useUpdateOverrideItemMutation,
  useDeleteOverrideItemMutation,
} = baseRateFilesQueries;
